/**
 * Kleda Client App - Order Summary Component
 * Persistent overzicht van de order: items, prijzen, upsells, betaalstatus
 *
 * Desktop: sidebar (altijd zichtbaar)
 * Mobile: inklapbare kaart onderaan main content
 */

import { useCallback, useState } from 'react'
import type { OrderData, WasserijItem, PricingBootstrap, AddVloerkleedRequest, EditVloerkleedRequest } from '../types'
import { SERVICE_PRODUCT_CODES } from '../types'
import { Icon, NummerBadge, VloerkleedToevoegenModal, type VloerkleedEditTarget } from './UI'
import { fetchPricingBootstrap, addVloerkleed, editVloerkleed, deleteVloerkleed } from '../api'
import { trackEvent } from '../analytics'
import { openBetaalLink } from '../lib/betaal'

// =============================================================================
// TYPES
// =============================================================================

interface OrderSummaryProps {
  order: OrderData
  className?: string
  onPaymentInitiated?: () => void
  isPaymentPolling?: boolean
  paymentTimedOut?: boolean
  resetPaymentTimedOut?: () => void
  /** Nodig voor het wijzigen van kleden (maten/vorm/pakket) vanuit het overzicht */
  token?: string
  onRefresh?: () => void
}

// =============================================================================
// HELPERS
// =============================================================================

function formatPrice(price: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
  }).format(price)
}

function isServiceProduct(item: WasserijItem): boolean {
  return SERVICE_PRODUCT_CODES.some(code => item.productCode === code)
}

interface VloerkleedGroepDisplay {
  primair: WasserijItem
  pakket?: WasserijItem
  extras: WasserijItem[]
}

function groupItems(items: WasserijItem[]) {
  const primaryItems: WasserijItem[] = []
  const packageItems: WasserijItem[] = []
  const extras: WasserijItem[] = []
  const services: WasserijItem[] = []

  for (const item of items) {
    if (isServiceProduct(item)) {
      services.push(item)
    } else if (item.productCategory === 'Primary') {
      primaryItems.push(item)
    } else if (item.productCategory === 'Package') {
      packageItems.push(item)
    } else {
      extras.push(item)
    }
  }

  // Group packages and extras with their primary item
  const groepen: VloerkleedGroepDisplay[] = primaryItems.map((primair) => {
    const pakket = packageItems.find(p => p.gerelateerdeItemId === primair.id)
    const itemExtras = extras.filter(e => e.gerelateerdeItemId === primair.id)
    return { primair, pakket, extras: itemExtras }
  })

  // Extras not linked to a specific primary item (order-level extras)
  const linkedExtraIds = new Set(groepen.flatMap(g => g.extras.map(e => e.id)))
  const orderExtras = extras.filter(e => !linkedExtraIds.has(e.id) && !e.gerelateerdeItemId)

  // Packages not linked to a specific primary item (fallback)
  const linkedPackageIds = new Set(groepen.map(g => g.pakket?.id).filter(Boolean))
  const unlinkedPackages = packageItems.filter(p => !linkedPackageIds.has(p.id))

  return { groepen, unlinkedPackages, orderExtras, services }
}

// Betaal-sectie pas tonen zodra de afspraak staat — in de kies-fase is
// plannen (+ Vlekkenbaas) de focus, daarna is betalen de enige open taak
const BETALEN_STATUSSEN = new Set([
  'Terugbrengen ingepland',
  'Teruggebracht',
  // Zelf-halen-pad: het kleed ligt klaar, betalen is de enige open taak
  'Wordt opgehaald',
  'Zelf opgehaald',
  'Afgerond',
])

// Zelfde venster als de SF-endpoints (canAddItems/canEditItems/canDeleteItems)
const BEWERKBARE_STATUSSEN = new Set([
  'Ophalen plannen',
  'Ophaalvoorstel gedaan',
  'Ophaalvoorstel geen reactie',
  'Ophalen ingepland',
])

// =============================================================================
// ORDER SUMMARY COMPONENT
// =============================================================================

/** Leid de modal-voorinvulling af uit een bestaand kleed in het overzicht */
function deriveEditTarget(groep: VloerkleedGroepDisplay, volgnummer: number): VloerkleedEditTarget {
  const p = groep.primair
  const base = {
    itemId: p.id,
    packageSku: groep.pakket?.productCode,
    volgnummer,
  }
  if (p.diameter) {
    return { ...base, vorm: 'rond', diameterCm: p.diameter }
  }
  if (p.lengte && p.breedte) {
    return { ...base, vorm: 'rechthoekig', lengteCm: p.lengte, breedteCm: p.breedte }
  }
  return { ...base, vorm: 'overig', oppervlakteM2: p.oppervlakte }
}

export function OrderSummary({ order, className = '', onPaymentInitiated, isPaymentPolling, paymentTimedOut, resetPaymentTimedOut, token, onRefresh }: OrderSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const { groepen, unlinkedPackages, orderExtras, services } = groupItems(order.items || [])
  const hasItems = order.items && order.items.length > 0

  // Kleed wijzigen/toevoegen/verwijderen — alleen vóór ophalen, server bewaakt dit ook
  const [editTarget, setEditTarget] = useState<VloerkleedEditTarget | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [pricing, setPricing] = useState<PricingBootstrap | null>(null)
  const [isLoadingPricing, setIsLoadingPricing] = useState(false)
  // Expliciete status-check: SF rekent 'Opgehaald'/'Aan het reinigen' nog
  // onder phaseType 'pickup', maar dan weigert de server mutaties al —
  // dus geen links meer tonen zodra het kleed is opgehaald.
  const kanBewerken = BEWERKBARE_STATUSSEN.has(order.status) && !!token && !!onRefresh

  const loadPricing = useCallback(async () => {
    if (!token || pricing) return
    setIsLoadingPricing(true)
    try {
      const result = await fetchPricingBootstrap(token)
      setPricing(result)
    } catch {
      // Error handled in modal
    } finally {
      setIsLoadingPricing(false)
    }
  }, [token, pricing])

  const handleEditGroep = useCallback((groep: VloerkleedGroepDisplay, volgnummer: number) => {
    if (!token) return
    setEditTarget(deriveEditTarget(groep, volgnummer))
    trackEvent('vloerkleed_edit_opened', { werkOrderId: order.werkOrderId })
    void loadPricing()
  }, [token, loadPricing, order.werkOrderId])

  const handleAddKleed = useCallback(() => {
    if (!token) return
    setShowAddModal(true)
    void loadPricing()
  }, [token, loadPricing])

  const handleEditVloerkleed = useCallback(async (request: Omit<EditVloerkleedRequest, 'token'>): Promise<boolean> => {
    if (!token) return false
    const result = await editVloerkleed({ ...request, token })
    if (result.success) {
      trackEvent('vloerkleed_edited', { werkOrderId: order.werkOrderId })
      onRefresh?.()
      return true
    }
    throw new Error(result.message || 'Er ging iets mis')
  }, [token, onRefresh, order.werkOrderId])

  const handleAddVloerkleed = useCallback(async (request: Omit<AddVloerkleedRequest, 'token'>): Promise<boolean> => {
    if (!token) return false
    const result = await addVloerkleed({ ...request, token })
    if (result.success) {
      onRefresh?.()
      setPricing(prev => prev ? {
        ...prev,
        bestaandeAantalKleden: prev.bestaandeAantalKleden + 1,
        bestaandeTotaalM2: result.nieuwTotaalM2 || prev.bestaandeTotaalM2,
      } : null)
      return true
    }
    throw new Error(result.message || 'Er ging iets mis')
  }, [token, onRefresh])

  const handleDeleteGroep = useCallback(async (groep: VloerkleedGroepDisplay): Promise<void> => {
    if (!token) return
    const result = await deleteVloerkleed({ token, itemId: groep.primair.id })
    if (result.success) {
      trackEvent('vloerkleed_deleted', { werkOrderId: order.werkOrderId })
      setPricing(null) // Tellingen kloppen niet meer; opnieuw laden bij volgende modal
      onRefresh?.()
      return
    }
    throw new Error(result.message || 'Er ging iets mis')
  }, [token, onRefresh, order.werkOrderId])

  if (!hasItems) return null

  const itemCount = groepen.length
  const itemLabel = itemCount === 1 ? 'vloerkleed' : 'vloerkleden'
  const isVolledigGecrediteerd =
    order.totaalInclBtw === 0 &&
    order.totaalOriginelePrijsInclBtw != null &&
    order.totaalOriginelePrijsInclBtw > 0

  return (
    <div className={className}>
      {/* Mobile: Collapsible */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full card flex items-center justify-between"
        >
          <div className="flex items-center gap-sm">
            <Icon name="rug" className="text-primary" />
            <div className="text-left">
              <p className="text-body font-semibold text-ink">
                {order.isVervolgWo ? 'Vervolg op uw klacht' : 'Jouw bestelling'}
              </p>
              <p className="text-body-sm text-ink-faint">
                {itemCount} {itemLabel}
                {isVolledigGecrediteerd ? (
                  <> · <span className="text-primary font-medium">Gratis</span></>
                ) : (
                  order.totaalInclBtw != null && (
                    <> · {formatPrice(order.totaalInclBtw)}</>
                  )
                )}
              </p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-ink-faint transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </button>

        {isExpanded && (
          <div className="card mt-xs">
            <OrderItemList
              groepen={groepen}
              unlinkedPackages={unlinkedPackages}
              orderExtras={orderExtras}
              services={services}
              totaalInclBtw={order.totaalInclBtw}
              totaalOriginelePrijsInclBtw={order.totaalOriginelePrijsInclBtw}
              totaleKortingInclBtw={order.totaleKortingInclBtw}
              isVervolgWo={order.isVervolgWo}
              onEditGroep={kanBewerken ? handleEditGroep : undefined}
              onDeleteGroep={kanBewerken ? handleDeleteGroep : undefined}
              onAddKleed={kanBewerken ? handleAddKleed : undefined}
            />
          </div>
        )}
      </div>

      {/* Desktop: Always visible sidebar */}
      <div className="hidden lg:block">
        <div className="card sticky top-4">
          <p className="section-label mb-md">
            {order.isVervolgWo ? 'Vervolg op uw klacht' : 'Jouw bestelling'}
          </p>
          {order.isVervolgWo && (
            <p className="text-body-sm text-ink-muted -mt-sm mb-md">
              Dit is een herreiniging, geen nieuwe opdracht.
            </p>
          )}

          <OrderItemList
            groepen={groepen}
            unlinkedPackages={unlinkedPackages}
            orderExtras={orderExtras}
            services={services}
            totaalInclBtw={order.totaalInclBtw}
            totaalOriginelePrijsInclBtw={order.totaalOriginelePrijsInclBtw}
            totaleKortingInclBtw={order.totaleKortingInclBtw}
            isVervolgWo={order.isVervolgWo}
            onEditGroep={kanBewerken ? handleEditGroep : undefined}
            onDeleteGroep={kanBewerken ? handleDeleteGroep : undefined}
            onAddKleed={kanBewerken ? handleAddKleed : undefined}
          />

          {/* Payment section — pas zodra de afspraak staat (kies-fase = focus op
              plannen + upsell; gepland = focus op betalen) */}
          {BETALEN_STATUSSEN.has(order.status) && !order.opFactuur && (order.openstaandBedrag ?? 0) > 0 && order.betaalStatus !== 'betaald' && (
            <PaymentBadge
              betaalLink={order.betaalLink}
              betaalStatus={order.betaalStatus}
              openstaandBedrag={order.openstaandBedrag}
              werkOrderId={order.werkOrderId}
              token={token}
              onRefresh={onRefresh}
              onPaymentInitiated={onPaymentInitiated}
              isPaymentPolling={isPaymentPolling}
              paymentTimedOut={paymentTimedOut}
              resetPaymentTimedOut={resetPaymentTimedOut}
            />
          )}
          {BETALEN_STATUSSEN.has(order.status) && order.betaalStatus === 'betaald' && (
            <div className="mt-sm flex items-center gap-xs text-secondary text-body-sm font-medium">
              <Icon name="check" className="w-4 h-4" />
              Betaald
            </div>
          )}
        </div>
      </div>

      {/* Kleed wijzigen/toevoegen modal */}
      {kanBewerken && (
        <VloerkleedToevoegenModal
          isOpen={!!editTarget || showAddModal}
          onClose={() => { setEditTarget(null); setShowAddModal(false) }}
          pricing={pricing}
          isLoadingPricing={isLoadingPricing}
          editTarget={editTarget}
          onEditVloerkleed={handleEditVloerkleed}
          onAddVloerkleed={handleAddVloerkleed}
          token={token || ''}
        />
      )}
    </div>
  )
}

// =============================================================================
// ORDER ITEM LIST (internal)
// =============================================================================

interface OrderItemListProps {
  groepen: VloerkleedGroepDisplay[]
  unlinkedPackages: WasserijItem[]
  orderExtras: WasserijItem[]
  services: WasserijItem[]
  totaalInclBtw?: number
  totaalOriginelePrijsInclBtw?: number
  totaleKortingInclBtw?: number
  /** Herbehandeling na klacht: kleurt de gratis-regel, geen creditering */
  isVervolgWo?: boolean
  /** Indien gezet: toont per kleed een "Wijzigen"-link (maten/vorm/pakket) */
  onEditGroep?: (groep: VloerkleedGroepDisplay, volgnummer: number) => void
  /** Indien gezet (en >1 kleed): toont per kleed een "Verwijderen"-link met inline bevestiging */
  onDeleteGroep?: (groep: VloerkleedGroepDisplay) => Promise<void>
  /** Indien gezet: toont een "+ Kleed toevoegen"-rij onder de items */
  onAddKleed?: () => void
}

function OrderItemList({
  groepen,
  unlinkedPackages,
  orderExtras,
  services,
  totaalInclBtw,
  totaalOriginelePrijsInclBtw,
  totaleKortingInclBtw,
  isVervolgWo,
  onEditGroep,
  onDeleteGroep,
  onAddKleed,
}: OrderItemListProps) {
  // Inline verwijder-bevestiging per kleed
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  async function handleConfirmDelete(groep: VloerkleedGroepDisplay) {
    if (!onDeleteGroep) return
    setDeletingId(groep.primair.id)
    setDeleteError(null)
    try {
      await onDeleteGroep(groep)
      setConfirmDeleteId(null)
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Er ging iets mis')
    } finally {
      setDeletingId(null)
    }
  }

  // Verwijderen alleen bij meerdere kleden — laatste kleed = annuleren-flow
  const kanVerwijderen = !!onDeleteGroep && groepen.length > 1

  return (
    <div className="space-y-sm">
      {/* Grouped items: carpet + package as text line + item extras */}
      {groepen.map((groep, idx) => (
        <div key={groep.primair.id} className="flex items-start justify-between gap-sm">
          <div className="flex-1 min-w-0">
            <p className="flex items-center gap-xs text-[15px] font-semibold text-ink">
              {groepen.length > 1 && <NummerBadge nummer={idx + 1} />}
              <span className="truncate">{groep.primair.productNaam}</span>
            </p>
            {/* Maat + pakket als stille tekstregel (geen pill) */}
            <p className="text-[13px] text-ink-faint">
              {groep.primair.oppervlakte != null && (
                <>
                  {groep.primair.oppervlakte.toFixed(1).replace('.', ',')} m²
                  {groep.primair.lengte && groep.primair.breedte && (
                    <> ({groep.primair.lengte}×{groep.primair.breedte} cm)</>
                  )}
                  {groep.primair.diameter && (
                    <> (ø{groep.primair.diameter} cm)</>
                  )}
                </>
              )}
              {groep.pakket && (
                <>{groep.primair.oppervlakte != null ? ' · ' : ''}{groep.pakket.productNaam}</>
              )}
            </p>
            {groep.extras.map((extra) => (
              <p key={extra.id} className="text-[13px] text-ink-soft">
                {extra.productNaam}
              </p>
            ))}
            {(onEditGroep || kanVerwijderen) && (
              confirmDeleteId === groep.primair.id ? (
                <p className="text-[12px] text-ink-soft">
                  Kleed verwijderen?{' '}
                  <button
                    type="button"
                    disabled={deletingId === groep.primair.id}
                    onClick={() => handleConfirmDelete(groep)}
                    className="font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                  >
                    {deletingId === groep.primair.id ? 'Verwijderen...' : 'Ja, verwijder'}
                  </button>
                  {' · '}
                  <button
                    type="button"
                    disabled={deletingId === groep.primair.id}
                    onClick={() => { setConfirmDeleteId(null); setDeleteError(null) }}
                    className="font-medium text-ink-muted hover:text-ink"
                  >
                    Nee
                  </button>
                </p>
              ) : (
                <p className="text-[12px]">
                  {onEditGroep && (
                    <button
                      type="button"
                      onClick={() => onEditGroep(groep, idx + 1)}
                      className="font-medium text-primary hover:text-primary-dark"
                    >
                      Wijzigen
                    </button>
                  )}
                  {onEditGroep && kanVerwijderen && <span className="text-ink-faint"> · </span>}
                  {kanVerwijderen && (
                    <button
                      type="button"
                      onClick={() => { setConfirmDeleteId(groep.primair.id); setDeleteError(null) }}
                      className="font-medium text-ink-faint hover:text-red-600"
                    >
                      Verwijderen
                    </button>
                  )}
                </p>
              )
            )}
            {deleteError && confirmDeleteId === groep.primair.id && (
              <p className="text-[12px] text-red-600 mt-xs">{deleteError}</p>
            )}
          </div>
          {groep.primair.prijsInclBtw != null && (
            <p className="text-body-sm font-medium text-ink whitespace-nowrap">
              {formatPrice(groep.primair.prijsInclBtw)}
            </p>
          )}
        </div>
      ))}

      {/* Unlinked packages (fallback if gerelateerdeItemId not set) */}
      {unlinkedPackages.length > 0 && unlinkedPackages.map((item) => (
        <div key={item.id} className="flex items-start justify-between gap-sm">
          <p className="text-[13px] text-ink-soft">{item.productNaam}</p>
          {item.prijsInclBtw != null && (
            <p className="text-body-sm text-ink whitespace-nowrap">
              {formatPrice(item.prijsInclBtw)}
            </p>
          )}
        </div>
      ))}

      {/* Order-level extra's (upsells not linked to a specific carpet) */}
      {orderExtras.length > 0 && (
        <>
          {orderExtras.map((item) => (
            <div key={item.id} className="flex items-start justify-between gap-sm border-t border-hairline pt-sm">
              <p className="text-[13.5px] text-ink-soft flex-1 min-w-0 truncate">
                {item.productNaam}
              </p>
              {item.prijsInclBtw != null && (
                <p className="text-body-sm text-ink whitespace-nowrap">
                  {formatPrice(item.prijsInclBtw)}
                </p>
              )}
            </div>
          ))}
        </>
      )}

      {/* Service products (wijzigkosten etc.) */}
      {services.length > 0 && (
        <>
          {services.map((item) => (
            <div key={item.id} className="flex items-start justify-between gap-sm text-ink-faint">
              <p className="text-body-xs truncate">{item.productNaam}</p>
              {item.prijsInclBtw != null && (
                <p className="text-body-xs whitespace-nowrap">
                  {formatPrice(item.prijsInclBtw)}
                </p>
              )}
            </div>
          ))}
        </>
      )}

      {/* Kleed toevoegen */}
      {onAddKleed && (
        <button
          type="button"
          onClick={onAddKleed}
          className="text-[13px] font-medium text-primary hover:text-primary-dark"
        >
          + Kleed toevoegen
        </button>
      )}

      {/* Totals */}
      <div className="border-t border-hairline pt-sm space-y-xs">
        {(() => {
          const isVolledigGecrediteerd =
            totaalInclBtw === 0 &&
            totaalOriginelePrijsInclBtw != null &&
            totaalOriginelePrijsInclBtw > 0

          if (isVolledigGecrediteerd) {
            return (
              <div className="pt-xs">
                <div className="flex items-center justify-between">
                  <span className="text-[14px] font-bold text-ink">
                    Totaal <span className="font-normal text-[11px] text-ink-faint">incl. btw</span>
                  </span>
                  <div className="text-right">
                    <span className="text-body-xs text-ink-faint line-through block">
                      {formatPrice(totaalOriginelePrijsInclBtw!)}
                    </span>
                    <span className="font-heading font-bold text-[17px] text-primary">
                      Gratis
                    </span>
                  </div>
                </div>
                <p className="text-body-xs text-ink-faint mt-xs text-right">
                  {isVervolgWo ? 'Herbehandeling, geen kosten' : 'Volledig gecrediteerd'}
                </p>
              </div>
            )
          }

          const heeftKorting = totaleKortingInclBtw != null && totaleKortingInclBtw > 0

          return (
            <>
              {/* Subtotaal + korting — alleen tonen als er korting is */}
              {heeftKorting && totaalOriginelePrijsInclBtw != null && (
                <div className="flex justify-between text-body-xs text-ink-soft">
                  <span>Subtotaal</span>
                  <span>{formatPrice(totaalOriginelePrijsInclBtw)}</span>
                </div>
              )}
              {heeftKorting && (
                <div className="flex justify-between text-body-xs text-secondary">
                  <span>Korting</span>
                  <span>-{formatPrice(totaleKortingInclBtw!)}</span>
                </div>
              )}

              {/* Total price — totaal in Komet */}
              {totaalInclBtw != null && (
                <div className="flex justify-between items-baseline pt-xs">
                  <span className="text-[14px] font-bold text-ink">
                    Totaal <span className="font-normal text-[11px] text-ink-faint">incl. btw</span>
                  </span>
                  <span className="font-heading font-bold text-[17px] text-ink">{formatPrice(totaalInclBtw)}</span>
                </div>
              )}
            </>
          )
        })()}
      </div>
    </div>
  )
}

// =============================================================================
// PAYMENT BADGE (internal)
// =============================================================================

interface PaymentBadgeProps {
  /** Kan ontbreken: openBetaalLink mint de link dan op het betaalmoment. */
  betaalLink?: string
  betaalStatus?: 'open' | 'betaald' | 'verlopen'
  openstaandBedrag?: number
  werkOrderId: string
  token?: string
  onRefresh?: () => void
  onPaymentInitiated?: () => void
  isPaymentPolling?: boolean
  paymentTimedOut?: boolean
  resetPaymentTimedOut?: () => void
}

function PaymentBadge({ betaalLink, betaalStatus, openstaandBedrag, werkOrderId, token, onRefresh, onPaymentInitiated, isPaymentPolling, paymentTimedOut, resetPaymentTimedOut }: PaymentBadgeProps) {
  const [geenLink, setGeenLink] = useState(false)
  async function handlePayClick() {
    // Ververs de link op het betaalmoment zelf, zodat kosten die ná het laden
    // zijn bijgekomen (opslag bij datumkeuze, upsell, kantoor-edit) meebetalen.
    const uitkomst = await openBetaalLink(token, betaalLink)
    if (uitkomst === 'betaald') {
      onRefresh?.()
      return
    }
    if (uitkomst === 'geen-link') {
      setGeenLink(true)
      return
    }
    onPaymentInitiated?.()
    trackEvent('payment_initiated', {
      werkOrderId,
      amount: openstaandBedrag,
    })
  }

  if (isPaymentPolling) {
    return (
      <div className="mt-sm pt-sm border-t border-hairline">
        <p className="text-body-sm font-medium text-blue-700">Betaling wordt verwerkt...</p>
      </div>
    )
  }

  if (paymentTimedOut) {
    return (
      <div className="mt-sm pt-sm border-t border-hairline">
        <p className="text-body-sm font-medium text-blue-700">Wordt nog verwerkt</p>
        <button
          type="button"
          onClick={() => resetPaymentTimedOut?.()}
          className="text-body-xs text-blue-700/70 hover:text-blue-900 mt-xs"
        >
          Opnieuw proberen
        </button>
      </div>
    )
  }

  return (
    <div className="mt-sm pt-sm border-t border-hairline">
      <div className="flex items-center justify-between gap-sm">
        <div>
          <p className="text-body-sm font-medium text-amber-700">
            {betaalStatus === 'verlopen' ? 'Betaallink verlopen' : 'Openstaand'}
          </p>
          {openstaandBedrag != null && (
            <p className="text-body-xs text-ink-faint">
              {formatPrice(openstaandBedrag)}
            </p>
          )}
          {geenLink && (
            <p className="text-body-xs text-ink-faint">Online betalen lukt nu even niet; pin of contant bij aflevering.</p>
          )}
        </div>
        <button
          type="button"
          onClick={handlePayClick}
          className="btn-primary py-xs px-md text-body-sm font-medium"
        >
          Betalen
        </button>
      </div>
    </div>
  )
}
